import { OwnOrders } from '@src/components/Ladder/OwnOrders';
import { PriceIncrement } from '@src/components/Ladder/PriceIncrement';
import type { ClickToTradePreviewOrder } from '@src/components/MarketDataCard/types';
import { useGetOrderCurrency } from '@src/components/OMS/useGetOrderCurrency';
import { getLevelPrice } from '@src/components/OrderBook/utils';
import { useIsPricingModeBPS, useMarketDataSnapshot, useOwnOrders, useTimeout } from '@src/hooks';
import type { OwnOrderResult } from '@src/hooks/useOwnOrders';
import { ORDER_SIDES } from '@src/tokens/order';
import {
  AggressorFlagEnum,
  InlineFormattedNumber,
  MixpanelEvent,
  MixpanelEventSource,
  NoDataMessage,
  PRICE,
  SideEnum,
  UnifiedLiquidityEnum,
  getPriceLadderSnapshotLevels,
  useMixpanel,
  useObservableValue,
  type MarketDataCardConfig,
  type MarketDataSnapshot,
  type OrderIntent,
  type Security,
} from '@talos/kyoko';
import Big from 'big.js';
import { isEqual } from 'lodash-es';
import { memo, useCallback, useMemo, type MouseEvent } from 'react';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import {
  BidBucket,
  BidsSideCell,
  HeaderRow,
  LadderBg,
  OfferBucket,
  OffersSideCell,
  Price,
  PxHeaderCell,
  QtyHeaderCell,
  Row,
  SpreadHeaderCell,
  Wrapper,
} from './styles';
import type { OnClickPriceFn } from './types';
import { getLadderBackgroundSize } from './utils';

const DEPTH = 5;

export interface PriceLadderProps {
  config: MarketDataCardConfig;
  security: Security;
  isReadOnly: boolean;
  activeOrder: ClickToTradePreviewOrder | undefined;
  onClickPrice: OnClickPriceFn;
  onMouseLeavePrice: () => any;
  onSaveCard: (config: MarketDataCardConfig) => any;
}

interface PriceRow {
  accBidSize: string;
  accOfferSize: string;
  bidDisplay?: string;
  bidPrice?: string;
  bidSize: string;
  bidBarSize: number;
  offerDisplay?: string;
  offerPrice?: string;
  offerSize: string;
  offerBarSize: number;
  levelBids: OwnOrderResult[];
  levelOffers: OwnOrderResult[];
}

export const PriceLadder = memo(function PriceLadder({
  config,
  security,
  isReadOnly,
  activeOrder,
  onClickPrice,
  onMouseLeavePrice,
  onSaveCard,
}: PriceLadderProps) {
  const {
    id,
    selectedMarkets,
    selectedMarketAccounts,
    unifiedLiquidityMarketSymbols,
    priceIncrement,
    symbol,
    unifiedLiquidity,
  } = config;
  const { SizeBuckets, MinPriceIncrement, DefaultPriceIncrement } = security;
  const sizeBucketMax = SizeBuckets[SizeBuckets.length - 1];
  const mixpanel = useMixpanel();
  const isPricingModeBPS = useIsPricingModeBPS(security);

  const { ownBids, ownOffers } = useOwnOrders({
    symbol,
    markets: selectedMarkets,
    marketAccounts: selectedMarketAccounts,
    includeUnifiedLiquidity: unifiedLiquidity === UnifiedLiquidityEnum.Enabled,
  });

  const memoizedPriceIncrement = useMemo(
    () => priceIncrement ?? DefaultPriceIncrement ?? '0.01',
    [priceIncrement, DefaultPriceIncrement]
  );

  const { marketDataSnapshots, marketDataErrors, isLoading } = useMarketDataSnapshot({
    markets: selectedMarkets,
    marketAccounts: selectedMarketAccounts,
    unifiedLiquidityMarketSymbols,
    priceIncrement: memoizedPriceIncrement,
    symbol,
    depth: DEPTH,
    tag: `${id}/${PRICE}`,
    currency: undefined,
    baseCurrency: undefined,
    includeTradeDirection: undefined,
    quantity: undefined,
    sizeBuckets: undefined,
    unifiedLiquidity,
  });

  const marketDataError = useObservableValue(() => marketDataErrors, [marketDataErrors]);

  const { rows, deepBids, deepOffers } = useObservableValue(
    () =>
      combineLatest<[MarketDataSnapshot, OwnOrderResult[], OwnOrderResult[]]>([
        marketDataSnapshots,
        ownBids,
        ownOffers,
      ]).pipe(
        map(([snapshot, ownBids, ownOffers]) => {
          const usedBids: Record<string, boolean> = {};
          const usedOffers: Record<string, boolean> = {};

          const bids = snapshot.Bids;
          const offers = snapshot.Offers;

          const rows: PriceRow[] = getPriceLadderSnapshotLevels(snapshot, DEPTH).map((row, index): PriceRow => {
            const accBidSize = row.bid.AccSize;
            const accOfferSize = row.offer.AccSize;
            const bidSize = row.bid.Size || '0';
            const offerSize = row.offer.Size || '0';

            // Use BPS for display when in BPS pricing mode, otherwise use Price
            const bidDisplay = getLevelPrice(bids[index], isPricingModeBPS);
            const bidPrice = isPricingModeBPS ? bids[index]?.BPS : bids[index]?.Price;
            const bidBarSize = getLadderBackgroundSize(bidSize, sizeBucketMax);
            const offerDisplay = getLevelPrice(offers[index], isPricingModeBPS);
            const offerPrice = isPricingModeBPS ? offers[index]?.BPS : offers[index]?.Price;
            const offerBarSize = getLadderBackgroundSize(offerSize, sizeBucketMax);

            const levelBid = bids[index]?.Price;
            const levelBids: OwnOrderResult[] = [];
            for (const o of ownBids) {
              const key = `${o.orderID}${o.market}`;
              if (levelBid && !usedBids[key] && Big(o.price ?? 0).gte(levelBid)) {
                levelBids.push(o);
                usedBids[key] = true;
              }
            }

            const levelOffer = offers[index]?.Price;
            const levelOffers: OwnOrderResult[] = [];
            for (const o of ownOffers) {
              const key = `${o.orderID}${o.market}`;
              if (levelOffer && !usedOffers[key] && Big(o.price ?? 0).lte(levelOffer)) {
                levelOffers.push(o);
                usedOffers[key] = true;
              }
            }

            return {
              accBidSize,
              accOfferSize,
              bidDisplay,
              bidPrice,
              bidSize,
              bidBarSize,
              offerDisplay,
              offerPrice,
              offerSize,
              offerBarSize,
              levelBids,
              levelOffers,
            };
          });

          const deepBids = ownBids.filter(o => !usedBids[`${o.orderID}${o.market}`]);
          const deepOffers = ownOffers.filter(o => !usedOffers[`${o.orderID}${o.market}`]);

          return { rows, deepBids, deepOffers };
        }),
        distinctUntilChanged((p, n) => isEqual(p, n))
      ),
    [marketDataSnapshots, ownBids, ownOffers, sizeBucketMax, isPricingModeBPS],
    { rows: [], deepBids: [], deepOffers: [] }
  );

  const handleIncrementChange = useCallback(
    (index: number, newIncrement: string) => {
      // Validate new increment is greater than the obligated minimum
      const priceIncrement = newIncrement < MinPriceIncrement ? MinPriceIncrement : newIncrement;
      const newConfig = { ...config, priceIncrement };
      mixpanel.track(MixpanelEvent.ChangeLadderIncrement);
      onSaveCard(newConfig);
    },
    [config, MinPriceIncrement, onSaveCard, mixpanel]
  );

  const timeout = useTimeout(2000);
  if (rows.length === 0 || marketDataError) {
    if (marketDataError) {
      return <NoDataMessage>{marketDataError.msg}</NoDataMessage>;
    }
    if (timeout || !isLoading) {
      return <NoDataMessage>No data available</NoDataMessage>;
    }

    return null;
  }

  return (
    <Wrapper data-testid="price-ladder">
      <PriceLadderHeader
        minPriceIncrement={MinPriceIncrement}
        priceIncrement={memoizedPriceIncrement}
        onIncrementChange={handleIncrementChange}
      />
      {rows.map((row, index) => {
        const isBidActive =
          activeOrder?.source === MixpanelEventSource.PriceLadder &&
          activeOrder.sourceIndex === index &&
          activeOrder.side === ORDER_SIDES.SELL;
        const isOfferActive =
          activeOrder?.source === MixpanelEventSource.PriceLadder &&
          activeOrder.sourceIndex === index &&
          activeOrder.side === ORDER_SIDES.BUY;

        const bidCell = {
          bidDisplay: row.bidDisplay,
          bidPrice: row.bidPrice,
          bidSize: row.bidSize,
          accBidSize: row.accBidSize,
          bidBarSize: row.bidBarSize,
          isReadOnly: isReadOnly || row.bidPrice == null,
          isActive: isBidActive,
        };
        const offerCell = {
          offerDisplay: row.offerDisplay,
          offerPrice: row.offerPrice,
          offerSize: row.offerSize,
          accOfferSize: row.accOfferSize,
          offerBarSize: row.offerBarSize,
          isReadOnly: isReadOnly || row.offerPrice == null,
          isActive: isOfferActive,
        };

        return (
          <Row
            key={index}
            activeSide={
              activeOrder?.source === MixpanelEventSource.PriceLadder &&
              activeOrder.sourceIndex === index &&
              activeOrder.side
            }
          >
            {bidCell.bidSize ? (
              <BidSide
                index={index}
                security={security}
                cell={bidCell}
                onClickPrice={onClickPrice}
                onMouseLeavePrice={onMouseLeavePrice}
              />
            ) : (
              <BidsSideCell isReadOnly={true} />
            )}
            {offerCell.offerSize && (
              <OfferSide
                index={index}
                security={security}
                cell={offerCell}
                onClickPrice={onClickPrice}
                onMouseLeavePrice={onMouseLeavePrice}
              />
            )}
            {/* We can't compare BPS on parent order with price on market orders so hide if in BPS pricing mode */}
            {!isPricingModeBPS && row.levelBids.length > 0 && <OwnOrders isBid ownOrders={row.levelBids} />}
            {!isPricingModeBPS && row.levelOffers.length > 0 && <OwnOrders isOffer ownOrders={row.levelOffers} />}
          </Row>
        );
      })}
      {/* We can't compare BPS on parent order with price on market orders so hide if in BPS pricing mode */}
      {!isPricingModeBPS && (
        <Row>
          {deepBids.length > 0 && <OwnOrders isBid ownOrders={deepBids} />}
          {deepOffers.length > 0 && <OwnOrders isOffer ownOrders={deepOffers} />}
        </Row>
      )}
    </Wrapper>
  );
});

interface PriceLadderHeaderProps {
  minPriceIncrement: string;
  priceIncrement: string;
  onIncrementChange: (index: number, newIncrement: string) => void;
}

const PriceLadderHeader = memo(function PriceLadderHeader({
  minPriceIncrement,
  priceIncrement,
  onIncrementChange,
}: PriceLadderHeaderProps) {
  return (
    <HeaderRow>
      <QtyHeaderCell $isLeftAligned>QTY</QtyHeaderCell>
      <PxHeaderCell $isLeftAligned>LMT PX</PxHeaderCell>
      <SpreadHeaderCell>
        <PriceIncrement min={minPriceIncrement} value={priceIncrement} onChange={onIncrementChange} />
      </SpreadHeaderCell>
      <PxHeaderCell $isRightAligned>LMT PX</PxHeaderCell>
      <QtyHeaderCell $isRightAligned>QTY</QtyHeaderCell>
    </HeaderRow>
  );
});

interface BidSideProps {
  index: number;
  security: Security;
  cell: {
    bidDisplay?: string;
    bidPrice?: string;
    accBidSize: string;
    bidSize?: string;
    bidBarSize: number;
    isActive: boolean;
    isReadOnly: boolean;
  };
  onClickPrice: OnClickPriceFn;
  onMouseLeavePrice: () => any;
}

const BidSide = memo(
  function BidSide({ index, security, cell, onClickPrice, onMouseLeavePrice }: BidSideProps) {
    const { bidDisplay, bidPrice, accBidSize, bidSize, bidBarSize, isActive, isReadOnly } = cell;

    const { getOrderCurrency } = useGetOrderCurrency();

    const handleClick = useCallback(
      (e: MouseEvent, intent: OrderIntent) => {
        e.preventDefault();
        if (!isReadOnly && bidPrice != null) {
          const side = intent === AggressorFlagEnum.Active ? SideEnum.Sell : SideEnum.Buy;
          const currency = getOrderCurrency(security, side);
          onClickPrice(
            {
              side,
              currency,
              orderQty: accBidSize,
              price: bidPrice,
              intent,
            },
            index
          );
        }
      },
      [accBidSize, index, isReadOnly, bidPrice, onClickPrice, security, getOrderCurrency]
    );

    return (
      <BidsSideCell
        isActive={isActive}
        onMouseLeave={onMouseLeavePrice}
        onClick={e => handleClick(e, AggressorFlagEnum.Active)}
        onContextMenu={e => handleClick(e, AggressorFlagEnum.Passive)}
        data-testid={`price-ladder-bid-${index}`}
      >
        <LadderBg
          isBid
          style={{
            transform: bidBarSize == null ? 'scaleX(0)' : `scaleX(${bidBarSize})`,
          }}
        />
        <BidBucket rawValue={bidSize} isReadOnly={true} precision={0} popOverPrecision={2} popOverPlacement="left" />
        <Price isReadOnly={isReadOnly}>
          <InlineFormattedNumber
            highlightAll={false}
            largeHighlight={true}
            isSmall
            number={bidDisplay}
            specification={security.PriceDisplaySpec}
          />
        </Price>
      </BidsSideCell>
    );
  },
  (p, n) => isEqual(p, n)
);

interface OfferSideProps {
  index: number;
  security: Security;
  cell: {
    offerDisplay?: string;
    offerPrice?: string;
    accOfferSize: string;
    offerSize?: string;
    offerBarSize: number;
    isActive: boolean;
    isReadOnly: boolean;
  };
  onClickPrice: OnClickPriceFn;
  onMouseLeavePrice: () => any;
}

const OfferSide = memo(
  function OfferSide({ index, security, cell, onClickPrice, onMouseLeavePrice }: OfferSideProps) {
    const { offerDisplay, offerPrice, accOfferSize, offerSize, offerBarSize, isActive, isReadOnly } = cell;

    const { getOrderCurrency } = useGetOrderCurrency();

    const handleClick = useCallback(
      (e: MouseEvent, intent: OrderIntent) => {
        e.preventDefault();
        if (!isReadOnly && offerPrice != null) {
          const side = intent === AggressorFlagEnum.Active ? SideEnum.Buy : SideEnum.Sell;
          const currency = getOrderCurrency(security, side);
          onClickPrice(
            {
              side,
              currency,
              orderQty: accOfferSize,
              price: offerPrice,
              intent,
            },
            index
          );
        }
      },
      [accOfferSize, index, isReadOnly, offerPrice, onClickPrice, security, getOrderCurrency]
    );

    return (
      <OffersSideCell
        isActive={isActive}
        onMouseLeave={onMouseLeavePrice}
        onClick={e => handleClick(e, AggressorFlagEnum.Active)}
        onContextMenu={e => handleClick(e, AggressorFlagEnum.Passive)}
        data-testid={`price-ladder-offer-${index}`}
      >
        <LadderBg
          isOffer
          style={{
            transform: offerBarSize == null ? 'scaleX(0)' : `scaleX(${offerBarSize})`,
          }}
        />
        <Price isReadOnly={isReadOnly}>
          <InlineFormattedNumber
            isSmall
            highlightAll={false}
            largeHighlight={true}
            number={offerDisplay}
            specification={security.PriceDisplaySpec}
          />
        </Price>
        <OfferBucket
          rawValue={offerSize}
          isReadOnly={true}
          precision={0}
          popOverPrecision={2}
          popOverPlacement="right"
        />
      </OffersSideCell>
    );
  },
  (p, n) => isEqual(p, n)
);
